/**
 * Qué pasa cuando alguien toca «Conectar» en el alta (UD-23 de `/ux`, 01/11).
 *
 * Hasta acá sólo se veía en el heatmap de Clarity: en el celular, 5 de 10 toques eran en «Conectar»
 * de Google y no quedaba ninguna conexión, y no había forma de saber si la persona eligió una cuenta
 * de Google sin Google Ads (`GOOGLE_NO_ADS_ACCESS`, el error más frecuente del paso), si canceló o si
 * nunca volvió. Cada toque y cada vuelta queda como un evento en `UserScreenActivity`:
 *
 * - `onboarding_connect_tap_<canal>`: tocó «Conectar» o «Reintentar».
 * - `onboarding_connect_ok_<canal>`: volvió conectado.
 * - `onboarding_connect_error_<canal>_<motivo>`: volvió con error (`no_ads_access`, `network` u `other`).
 * - `onboarding_connect_cancel_<canal>`: canceló (Meta lo avisa sin salir de la página; Google vuelve con
 *   `error=access_denied` y llega como `oauth_reason=CANCELLED`).
 * - `onboarding_connect_inapp_<canal>`: se suma al toque cuando es dentro del navegador de una app (Instagram,
 *   Facebook, la app de Google o un webview de Android). Ahí Meta y Google suelen no completar (Meta redirige
 *   a facebook.com y a la vuelta nadie procesa el código; Google bloquea el OAuth en un webview), así que esos
 *   toques se leen aparte. El toque se cuenta igual en `_tap_`: el criterio de UD-23 no cambia.
 * - `_error_<canal>_no_url`: no llegó a salir hacia el proveedor (falló el pedido de la dirección de Google);
 *   `_error_facebook_sdk`: el SDK de Meta no cargó (un bloqueador o la red lenta). Antes, lo primero no dejaba
 *   ningún evento y lo segundo se contaba como cancelado.
 *
 * Un toque sin ninguna vuelta el mismo día es «se perdió en el proveedor».
 */

const KNOWN_CHANNELS = ['google', 'facebook', 'tiktok', 'linkedin'];
export const CANCELLED = 'CANCELLED';
export const NO_AUTH_URL = 'NO_AUTH_URL';
export const FACEBOOK_SDK_NOT_LOADED = 'FACEBOOK_SDK_NOT_LOADED';
const REASONS = {
    GOOGLE_NO_ADS_ACCESS: 'no_ads_access', NETWORK_ERROR: 'network', [NO_AUTH_URL]: 'no_url',
    [FACEBOOK_SDK_NOT_LOADED]: 'sdk',
};

// La misma clasificación que la lectura de las altas por navegador (user agent del alta, 31/10).
const IN_APP_BROWSER = /Instagram|FBAN|FBAV|FB_IAB|FB4A|FBIOS|GSA\/|; wv\)/;

export function isInAppBrowser(userAgent = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
    return IN_APP_BROWSER.test(userAgent || '');
}

function channelSlug(channel) {
    const slug = String(channel || '').toLowerCase();
    return KNOWN_CHANNELS.includes(slug) ? slug : 'other';
}

export function connectTapEvent(channel) {
    return `onboarding_connect_tap_${channelSlug(channel)}`;
}

/** Los eventos de un toque: el toque y, dentro del navegador de una app, también la marca `_inapp_`. */
export function connectTapEvents(channel, userAgent) {
    const events = [connectTapEvent(channel)];
    if (isInAppBrowser(userAgent)) events.push(`onboarding_connect_inapp_${channelSlug(channel)}`);
    return events;
}

export function connectCancelEvent(channel) {
    return `onboarding_connect_cancel_${channelSlug(channel)}`;
}

export function connectErrorEvent(channel, reason) {
    return `onboarding_connect_error_${channelSlug(channel)}_${REASONS[reason] || 'other'}`;
}

/** El evento de la vuelta del OAuth, leído de la URL (`just_connected` / `oauth_error` + `oauth_reason`). */
export function connectReturnEvent({connected, failed, reason}) {
    if (connected) return `onboarding_connect_ok_${channelSlug(connected)}`;
    // Google vuelve con `oauth_reason=CANCELLED` cuando la persona canceló (`ConnectAdwordsAccount`).
    if (failed && reason === CANCELLED) return connectCancelEvent(failed);
    if (failed) return connectErrorEvent(failed, reason);
    return null;
}
